import {
  GetMyRunsDocument,
  StartMyUserRunDocument,
  type StartMyUserRunMutation,
  type StartMyUserRunMutationVariables,
} from "../services/__generated__/graphql";
import { apolloClient } from "../services/backend_gateway/apollo-client";
import type { useSystemMessages } from "../hooks/useSystemMessages";

type AddMessage = ReturnType<typeof useSystemMessages>["addMessage"];

export async function fireRun(args: {
  inputId: string;
  settingsName: string;
  workflowId: string;
  addMessage: AddMessage;
  /** Reuse a caller-owned identity when pending UI state must match the run row. */
  runAttemptId?: string;
  /** Let a caller clear its pending state after the shared error toast is emitted. */
  rethrowOnError?: boolean;
  /** Used in the success-toast description; defaults to `<settingsName> • input <inputId>`. */
  successDescription?: string;
}): Promise<void> {
  const {
    inputId,
    settingsName,
    workflowId,
    addMessage,
    successDescription,
    rethrowOnError = false,
  } = args;
  const runAttemptId = args.runAttemptId ?? new Date().toISOString();
  try {
    const { data } = await apolloClient.mutate<
      StartMyUserRunMutation,
      StartMyUserRunMutationVariables
    >({
      mutation: StartMyUserRunDocument,
      variables: {
        runSettings: { inputId, workflowId, settingsName, runAttemptId },
      },
    });
    if (!data?.startMyUserRun) {
      throw new Error("Run was not started — check permissions or customer selection.");
    }
    addMessage({
      type: "success",
      title: "Run started",
      description: successDescription ?? `${settingsName} • input ${inputId}`,
      autoDismiss: true,
      dismissAfterMs: 4000,
    });
  } catch (err) {
    addMessage({
      type: "error",
      title: "Failed to start run",
      description: err instanceof Error ? err.message : String(err),
      autoDismiss: true,
      dismissAfterMs: 6000,
    });
    if (rethrowOnError) throw err;
  } finally {
    // Refetch on both success and failure: success surfaces the new
    // RUN_IN_PROGRESS row written by the orchestrator, failure surfaces
    // the RUN_FAILED row written by the gateway resolver fallback.
    apolloClient
      .query({ query: GetMyRunsDocument, fetchPolicy: "network-only" })
      .catch((refetchErr) => console.error("Refetch GetMyRuns failed", refetchErr));
  }
}
